import { useState } from 'react'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { SortableList } from '~/components/shared/SortableList'
import { newEnumOptionFormValue } from './enum-option-form-values'
import { EnumOptionEditPopover } from './EnumOptionEditPopover'
import { EnumOptionRow } from './EnumOptionRow'
import { EnumOptionFormValue } from './types'

interface EnumOptionsEditorProps {
  options: EnumOptionFormValue[]
  onChange: (options: EnumOptionFormValue[]) => void
  /** A multi-select column renders its values as tinted chips rather than a dot and label */
  isMultiSelect: boolean
}

const labelsExcluding = (options: EnumOptionFormValue[], excludedRowId?: string): string[] =>
  options.filter((option) => option.rowId !== excludedRowId).map((option) => option.label)

// Reorderable list of an enum column's options, each editable in a popover
export const EnumOptionsEditor = ({ options, onChange, isMultiSelect }: EnumOptionsEditorProps) => {
  const { t } = useTypedTranslation()
  // An option being added exists only here until it is saved, so cancelling leaves no empty row behind
  const [newOption, setNewOption] = useState<EnumOptionFormValue | null>(null)

  const replaceOption = (index: number, updated: EnumOptionFormValue) => {
    onChange(options.map((option, i) => (i === index ? updated : option)))
  }

  return (
    <div>
      <SortableList
        getItemId={(option) => option.rowId}
        items={options}
        onReorder={onChange}
        renderItem={(option, index) => (
          <EnumOptionRow
            key={option.rowId}
            isMultiSelect={isMultiSelect}
            onChange={(updated) => replaceOption(index, updated)}
            onDelete={() => onChange(options.filter((_, i) => i !== index))}
            option={option}
            otherLabels={labelsExcluding(options, option.rowId)}
          />
        )}
      />

      <EnumOptionEditPopover
        isMultiSelect={isMultiSelect}
        isOpen={!!newOption}
        onOpenChange={(isOpen) => setNewOption(isOpen ? newEnumOptionFormValue() : null)}
        onSave={(option) => onChange([...options, option])}
        option={newOption}
        otherLabels={labelsExcluding(options)}
        triggerClassName="px-0"
        triggerContent={`+ ${t('table.create_column.add_option')}`}
      />
    </div>
  )
}
